import { useAuth } from '@devd-client/devd/auth';
import {
  useQueryState,
  durationData,
  removeEmptyQueryParams,
  useDashboardStore,
  useGlobalState,
  PeriodType,
  MetricTooltip,
} from '@devd-client/devd/components';
import { OptionBase } from 'chakra-react-select';
import moment from 'moment';
import { useState, useEffect, useMemo } from 'react';
import { useFetchMetricList } from '../api';
import { getCompleteMetricList } from '../utils/team-table.utils';
export interface Option extends OptionBase {
  label: string;
  value: string;
}

interface MetricState {
  metricTitle: string;
  metricKey: string;
  sequence: number;
  id?: string;
}

export const useTeamTable = () => {
  const { globalState } = useGlobalState();
  const { selected, selectedTeam, setSelected, setSelectedTeam } =
    useDashboardStore();

  const [startDate, setStartDate] = useQueryState('start');
  const [endDate, setEndDate] = useQueryState('end');
  const [duration, setDuration] = useQueryState('duration');
  const [sprintId, setSprintId] = useQueryState('sprintId');
  const [sprintName, setSprintName] = useQueryState('sprint');
  const [team, setTeam] = useQueryState('team');
  const [orderByUrl, setOrderByUrl] = useQueryState('orderBy');
  const [orderColumnUrl, setOrderColumnUrl] = useQueryState('orderColumn');
  const [searchTerm, setSearchTerm] = useState('');
  const [compareToUrl, setCompareToUrl] = useQueryState('compareTo');
  const { data: metricList } = useFetchMetricList();

  const [selectedMetrics, setSelectedMetrics] = useState<MetricState[]>([]);
  const [selectedPermanentColumns, setSelectedPermanentColumns] = useState<
    string[]
  >(['Teams', 'Members']);
  const [orderBy, setOrderBy] = useState<string>();
  const [orderColumn, setOrderColumn] = useState<string>();
  const [compareTo, setCompareTo] = useState<
    'pastPeriod' | 'contributor' | 'none'
  >('none');

  const [appState, dispatch] = useAuth();
  const [teamLength, setTeamLength] = useState(0);
  const [periodOptions, setPeriodOptions] =
    useState<PeriodType[]>(durationData);

  useEffect(() => {
    if (typeof selected !== 'undefined') {
      selected.startDate && setStartDate(selected.startDate);
      selected.endDate && setEndDate(selected.endDate);
      selected.duration && setDuration(selected.duration);
      selected?.sprintId === ''
        ? removeEmptyQueryParams({ sprintId: '' })
        : setSprintId(selected?.sprintId);
      selected?.sprintName === ''
        ? removeEmptyQueryParams({ sprint: '' })
        : setSprintName(selected?.sprintName);
    }

    if (typeof selectedTeam !== 'undefined') {
      selectedTeam.label === 'Organisation'
        ? setTeam('Organisation')
        : setTeam(selectedTeam.value);
    }
  }, [selected, searchTerm, selectedTeam.value]);

  useEffect(() => {
    team &&
      setSelectedTeam(
        team === 'Organisation'
          ? { value: '', label: 'Organisation' }
          : { value: team, label: team }
      );

    duration || selected.duration
      ? setSelected(
          periodOptions
            .filter(
              (item: PeriodType) =>
                item.duration === duration ||
                item.duration === selected.duration
            )
            .map((el: PeriodType) =>
              el.duration === 'Custom' ||
              el.duration === 'Today' ||
              el.duration === 'Month' ||
              el.duration === 'Quarter'
                ? {
                    ...el,
                    startDate: startDate || selected.startDate,
                    endDate: endDate || selected.endDate,
                  }
                : el.duration === 'Sprint'
                ? {
                    ...el,
                    startDate: startDate || selected.startDate,
                    endDate: endDate || selected.endDate,
                    sprintId: sprintId || selected.sprintId,
                    sprintName: sprintName || selected.sprintName,
                  }
                : el
            )[0]
        )
      : appState?.user?.dto?.sprint
      ? setSelected({
          ...periodOptions[6],
          startDate: moment(
            JSON.parse(appState?.user?.dto?.sprintDetail)?.startDate
          ).format('YYYY-MM-DD'),
          endDate: moment(
            JSON.parse(appState?.user?.dto?.sprintDetail)?.endDate
          ).format('YYYY-MM-DD'),
          sprintId: appState?.user?.dto?.sprint,
          sprintName: JSON.parse(appState?.user?.dto?.sprintDetail)?.sprintName,
        })
      : setSelected(periodOptions[3]);
  }, []);

  useEffect(() => {
    if (typeof orderBy !== 'undefined') {
      orderBy === ''
        ? removeEmptyQueryParams({ orderBy: '' })
        : setOrderByUrl(orderBy);
    }
    if (typeof orderColumn !== 'undefined') {
      orderColumn === ''
        ? removeEmptyQueryParams({ orderColumn: '' })
        : setOrderColumnUrl(orderColumn);
    }

    if (typeof compareTo !== 'undefined') {
      compareTo === 'none'
        ? removeEmptyQueryParams({ compareTo: '' })
        : setCompareToUrl(compareTo);
    }
  }, [
    selected,
    selectedTeam.value,
    searchTerm,
    selectedMetrics,
    orderBy,
    orderColumn,
    compareTo,
  ]);

  useEffect(() => {
    team &&
      setSelectedTeam(
        team === 'Organisation'
          ? { value: '', label: 'Organisation' }
          : { value: team, label: team }
      );

    orderByUrl && setOrderBy(orderByUrl);
    orderColumnUrl && setOrderColumn(orderColumnUrl);
    compareToUrl &&
      setCompareTo(compareToUrl as 'pastPeriod' | 'contributor' | 'none');

    duration || selected.duration
      ? setSelected(
          periodOptions
            .filter(
              (item: PeriodType) =>
                item.duration === duration ||
                item.duration === selected.duration
            )
            .map((el: PeriodType) =>
              el.duration === 'Custom' ||
              el.duration === 'Today' ||
              el.duration === 'Month' ||
              el.duration === 'Quarter'
                ? {
                    ...el,
                    startDate: startDate || selected.startDate,
                    endDate: endDate || selected.endDate,
                  }
                : el.duration === 'Sprint'
                ? {
                    ...el,
                    startDate: startDate || selected.startDate,
                    endDate: endDate || selected.endDate,
                    sprintId: sprintId || selected.sprintId,
                    sprintName: sprintName || selected.sprintName,
                  }
                : el
            )[0]
        )
      : appState?.user?.dto?.sprint
      ? setSelected({
          ...periodOptions[6],
          startDate: moment(
            JSON.parse(appState?.user?.dto?.sprintDetail)?.startDate
          ).format('YYYY-MM-DD'),
          endDate: moment(
            JSON.parse(appState?.user?.dto?.sprintDetail)?.endDate
          ).format('YYYY-MM-DD'),
          sprintId: appState?.user?.dto?.sprint,
          sprintName: JSON.parse(appState?.user?.dto?.sprintDetail)?.sprintName,
        })
      : setSelected(periodOptions[3]);
  }, []);

  const processedMetrics = useMemo(() => {
    if (!metricList?.dto) return [];

    return metricList.dto.flatMap((metric: MetricTooltip) => {
      if (metric.isPerMemberMetric) {
        const memberMetric = {
          ...metric,
          id: `${metric.metric}_PER_MEMBER`,
          title: `${metric.title}/Member`,
          info: `${metric.info} (Avg per member)`,
        };
        return [
          {
            ...metric,
            id: metric.metric === 'CUSTOM' ? metric.title : metric.metric,
          },
          memberMetric,
        ];
      }
      return [
        {
          ...metric,
          id: metric.metric === 'CUSTOM' ? metric.title : metric.metric,
        },
      ];
    });
  }, [metricList?.dto]);

  useEffect(() => {
    if (!processedMetrics) return;
    if (globalState?.dto?.teams_metrics) {
      const allMetrics = getCompleteMetricList(processedMetrics);
      setSelectedMetrics(
        JSON.parse(globalState?.dto?.teams_metrics)
          ?.flatMap((id: string) => {
            return allMetrics?.filter((el: any) => el.id === id);
          })
          .map((item: MetricTooltip, index: number) => ({
            metricTitle: item?.title ?? '',
            metricKey: item?.metric,
            sequence: index,
            id: item?.id,
          }))
      );
      const permanentColumns = JSON.parse(
        globalState?.dto?.teams_metrics
      )?.filter((item: string) => ['Members'].includes(item));
      setSelectedPermanentColumns(['Teams', ...permanentColumns]);
    } else {
      setSelectedMetrics([
        {
          sequence: 1,
          metricKey: 'CONTRIBUTOR_COUNT',
          metricTitle: 'Number of Contributors',
          id: 'CONTRIBUTOR_COUNT',
        },
        {
          sequence: 2,
          metricKey: 'GIT_ACTIVITY',
          metricTitle: 'Git Activity',
          id: 'GIT_ACTIVITY',
        },
        {
          sequence: 3,
          metricKey: 'ISSUE_ACTIVITY',
          metricTitle: 'Issue Activity',
          id: 'ISSUE_ACTIVITY',
        },
        {
          sequence: 4,
          metricKey: 'DORA_ACTIVITY',
          metricTitle: 'DORA Activity',
          id: 'DORA_ACTIVITY',
        },
        {
          sequence: 5,
          metricKey: 'AI_ADOPTION',
          metricTitle: 'AI Adoption',
          id: 'AI_ADOPTION',
        },
      ]);
    }
  }, [processedMetrics, globalState?.dto?.teams_metrics]);

  return {
    selected,
    selectedTeam,
    setSelected,
    setSelectedTeam,
    teamLength,
    searchTerm,
    setSearchTerm,
    selectedMetrics,
    setSelectedMetrics,
    selectedPermanentColumns,
    setSelectedPermanentColumns,
    orderBy,
    setOrderBy,
    orderColumn,
    setOrderColumn,
    setOrderByUrl,
    setOrderColumnUrl,
    compareTo,
    setCompareTo,
    appState,
    dispatch,
    processedMetrics,
  };
};
